import { useState, useMemo } from 'react';
import { Helmet } from 'react-helmet';
import Header from '../../components/Header';
import Footer from '../../components/Footer';
import MobileEmergencyBar from '../../components/MobileEmergencyBar';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import TestimonialCard from './components/TestimonialCard';
import BeforeAfterSlider from './components/BeforeAfterSlider';
import FilterBar from './components/FilterBar';
import StatsOverview from './components/StatsOverview';
import CommercialHighlight from './components/CommercialHighlight';

const ClientSuccessStories = () => {
  const [activeFilters, setActiveFilters] = useState({
    clientType: 'all',
    serviceType: 'all',
    complexity: 'all'
  });

  const testimonials = [
  {
    id: 1,
    clientName: "Sarah Mitchell",
    clientRole: "Homeowner",
    clientImage: "https://img.rocket.new/generatedImages/rocket_gen_img_1ffde516e-1763293580273.png",
    clientImageAlt: "Professional woman with shoulder-length brown hair wearing white blouse smiling warmly at camera in bright office setting",
    companyName: null,
    serviceType: "Emergency Plumbing",
    rating: 5,
    testimonialText: "Burst pipe at 2 AM and Gert was at my door within 45 minutes. Not only did he fix the immediate problem, but he also identified a potential issue with my water heater that could have caused major damage. His 30+ years of experience really shows in how quickly he diagnosed everything. Fair pricing and exceptional workmanship.",
    projectDetails: "Emergency leak repair and preventive maintenance",
    date: "December 2025",
    isCommercial: false,
    complexity: "emergency",
    service: "plumbing"
  },
  {
    id: 2,
    clientName: "David Chen",
    clientRole: "Facilities Manager",
    clientImage: "https://img.rocket.new/generatedImages/rocket_gen_img_183fc715d-1763301362797.png",
    clientImageAlt: "Asian businessman in navy suit and glasses with confident smile standing in modern corporate office with glass walls",
    companyName: "McDonald\'s Regional",
    serviceType: "Commercial Plumbing",
    rating: 5,
    testimonialText: "We've worked with Gert on 15+ McDonald's locations. His ability to complete complex installations without disrupting business operations is remarkable. He understands the urgency of commercial work and always delivers on time. The quality of his work means we rarely have callbacks, which is crucial for our operations.",
    projectDetails: "Multi-location plumbing installations and maintenance",
    date: "November 2025",
    isCommercial: true,
    complexity: "complex",
    service: "plumbing"
  },
  {
    id: 3,
    clientName: "Jennifer Adams",
    clientRole: "Property Owner",
    clientImage: "https://img.rocket.new/generatedImages/rocket_gen_img_13f238fa2-1763296134776.png",
    clientImageAlt: "Professional blonde woman in elegant black blazer with warm smile in contemporary office space with natural lighting",
    companyName: null,
    serviceType: "Electrical Diagnostics",
    rating: 5,
    testimonialText: "Had intermittent electrical issues that three other electricians couldn't solve. Gert spent the time to properly diagnose the problem - turned out to be a complex wiring issue from a previous renovation. His methodical approach and decades of experience made all the difference. Haven't had a single issue since.",
    projectDetails: "Complex electrical fault finding and rewiring",
    date: "October 2025",
    isCommercial: false,
    complexity: "complex",
    service: "electrical"
  },
  {
    id: 4,
    clientName: "Marcus Williams",
    clientRole: "Restaurant Manager",
    clientImage: "https://images.unsplash.com/photo-1707501766995-b7f6dc263ba1",
    clientImageAlt: "African American man in professional chef whites with confident expression in commercial kitchen with stainless steel equipment",
    companyName: "KFC Franchise",
    serviceType: "Emergency Electrical",
    rating: 5,
    testimonialText: "Kitchen electrical failure during lunch rush - Gert arrived within 30 minutes and had us back operational in under 2 hours. His emergency response saved us thousands in lost revenue. We now have him on retainer for all our locations. Absolutely reliable and professional.",
    projectDetails: "Emergency electrical repair and system upgrade",
    date: "January 2026",
    isCommercial: true,
    complexity: "emergency",
    service: "electrical"
  },
  {
    id: 5,
    clientName: "Lisa Thompson",
    clientRole: "Homeowner",
    clientImage: "https://img.rocket.new/generatedImages/rocket_gen_img_154a96764-1763300123486.png",
    clientImageAlt: "Middle-aged woman with short auburn hair wearing casual blue sweater with friendly smile in cozy home interior",
    companyName: null,
    serviceType: "Bathroom Renovation",
    rating: 5,
    testimonialText: "Complete bathroom plumbing renovation. Gert's attention to detail and quality workmanship exceeded expectations. He explained everything clearly, stayed within budget, and the work was completed on schedule. The peace of mind knowing it's done right by someone with 30+ years experience is invaluable.",
    projectDetails: "Full bathroom plumbing installation",
    date: "September 2025",
    isCommercial: false,
    complexity: "standard",
    service: "plumbing"
  },
  {
    id: 6,
    clientName: "Robert Foster",
    clientRole: "Operations Director",
    clientImage: "https://img.rocket.new/generatedImages/rocket_gen_img_134fa37c4-1763294191074.png",
    clientImageAlt: "Senior executive with gray hair in formal dark suit and tie with professional demeanor in executive boardroom setting",
    companyName: "Retail Chain",
    serviceType: "Commercial Electrical",
    rating: 5,
    testimonialText: "Gert handled the electrical installations for our new store openings. His project management skills and ability to coordinate with other contractors made the process seamless. The quality of work and his professional approach to commercial projects sets him apart from other contractors we\'ve worked with.",
    projectDetails: "Multi-store electrical installations",
    date: "August 2025",
    isCommercial: true,
    complexity: "complex",
    service: "electrical"
  },
  {
    id: 7,
    clientName: "Emily Rodriguez",
    clientRole: "Homeowner",
    clientImage: "https://img.rocket.new/generatedImages/rocket_gen_img_124d3a6d9-1763301454658.png",
    clientImageAlt: "Hispanic woman with long dark hair in casual white top with bright smile in modern home kitchen with natural light",
    companyName: null,
    serviceType: "Water Heater Installation",
    rating: 5,
    testimonialText: "Old water heater failed and Gert not only replaced it same day but also upgraded the entire system for better efficiency. His recommendations were spot-on and have already reduced our energy bills. Professional, courteous, and clearly knows his trade inside and out.",
    projectDetails: "Water heater replacement and system upgrade",
    date: "December 2025",
    isCommercial: false,
    complexity: "standard",
    service: "plumbing"
  },
  {
    id: 8,
    clientName: "Thomas Baker",
    clientRole: "Facility Supervisor",
    clientImage: "https://img.rocket.new/generatedImages/rocket_gen_img_1078b8bc3-1763296698021.png",
    clientImageAlt: "Caucasian man in safety vest and hard hat with clipboard in industrial facility with machinery in background",
    companyName: "Manufacturing Plant",
    serviceType: "Industrial Electrical",
    rating: 5,
    testimonialText: "Complex electrical diagnostics in our production facility. Gert's systematic approach and deep understanding of industrial systems identified issues that could have caused major downtime. His preventive recommendations have saved us significant costs. Highly recommend for commercial and industrial work.",
    projectDetails: "Industrial electrical diagnostics and repairs",
    date: "November 2025",
    isCommercial: true,
    complexity: "complex",
    service: "electrical"
  }];


  const beforeAfterProjects = [
  {
    id: 1,
    title: "Commercial Kitchen Plumbing Overhaul",
    description: "Complete plumbing system replacement for busy restaurant kitchen",
    serviceType: "Commercial Plumbing",
    duration: "3 Days",
    beforeImage: "https://images.unsplash.com/photo-1645055005210-e9887dec1abf",
    beforeImageAlt: "Old deteriorating commercial kitchen with rusty pipes, water stains on walls, damaged fixtures, and outdated plumbing infrastructure",
    afterImage: "https://images.unsplash.com/photo-1609767090084-ad5348a00fad",
    afterImageAlt: "Modern renovated commercial kitchen with gleaming stainless steel fixtures, new copper piping, pristine white walls, and professional-grade plumbing installation",
    problem: "Frequent leaks and low water pressure",
    solution: "Complete pipe replacement with modern fixtures",
    result: "Zero leaks, improved efficiency",
    complexity: "complex",
    service: "plumbing",
    isCommercial: true
  },
  {
    id: 2,
    title: "Residential Electrical Panel Upgrade",
    description: "Safety upgrade from outdated fuse box to modern circuit breaker system",
    serviceType: "Electrical Upgrade",
    duration: "1 Day",
    beforeImage: "https://images.unsplash.com/photo-1675976942582-0272a67c6f21",
    beforeImageAlt: "Old rusty electrical fuse box with exposed wiring, corroded metal casing, outdated ceramic fuses, and visible safety hazards on aged wall",
    afterImage: "https://img.rocket.new/generatedImages/rocket_gen_img_1e1a56357-1766961752370.png",
    afterImageAlt: "New modern electrical panel with organized circuit breakers, clean white housing, properly labeled switches, and professional cable management on fresh wall",
    problem: "Outdated and unsafe electrical system",
    solution: "Modern panel with safety features",
    result: "Enhanced safety and capacity",
    complexity: "standard",
    service: "electrical",
    isCommercial: false
  },
  {
    id: 3,
    title: "Emergency Burst Pipe Repair",
    description: "Rapid response to major water leak in residential property",
    serviceType: "Emergency Plumbing",
    duration: "4 Hours",
    beforeImage: "https://img.rocket.new/generatedImages/rocket_gen_img_128e5d275-1768067209849.png",
    beforeImageAlt: "Flooded basement with standing water covering floor, damaged drywall with water stains, burst copper pipe spraying water, and water-damaged furniture",
    afterImage: "https://img.rocket.new/generatedImages/rocket_gen_img_13e2be0ad-1767656413444.png",
    afterImageAlt: "Dry renovated basement with new flooring, repaired walls with fresh paint, professionally installed copper piping, and restored living space",
    problem: "Major water damage from burst pipe",
    solution: "Emergency repair and water damage prevention",
    result: "Immediate leak stop, minimal damage",
    complexity: "emergency",
    service: "plumbing",
    isCommercial: false
  }];


  const filteredTestimonials = useMemo(() => {
    return testimonials?.filter((testimonial) => {
      const clientTypeMatch = activeFilters?.clientType === 'all' ||
      activeFilters?.clientType === 'commercial' && testimonial?.isCommercial ||
      activeFilters?.clientType === 'residential' && !testimonial?.isCommercial;

      const serviceTypeMatch = activeFilters?.serviceType === 'all' ||
      testimonial?.service === activeFilters?.serviceType;

      const complexityMatch = activeFilters?.complexity === 'all' ||
      testimonial?.complexity === activeFilters?.complexity;

      return clientTypeMatch && serviceTypeMatch && complexityMatch;
    });
  }, [activeFilters, testimonials]);

  const filteredProjects = useMemo(() => {
    return beforeAfterProjects?.filter((project) => {
      const clientTypeMatch = activeFilters?.clientType === 'all' ||
      activeFilters?.clientType === 'commercial' && project?.isCommercial ||
      activeFilters?.clientType === 'residential' && !project?.isCommercial;

      const serviceTypeMatch = activeFilters?.serviceType === 'all' ||
      project?.service === activeFilters?.serviceType;

      const complexityMatch = activeFilters?.complexity === 'all' ||
      project?.complexity === activeFilters?.complexity;

      return clientTypeMatch && serviceTypeMatch && complexityMatch;
    });
  }, [activeFilters, beforeAfterProjects]);

  const handleFilterChange = (category, value) => {
    if (category === 'reset') {
      setActiveFilters({
        clientType: 'all',
        serviceType: 'all',
        complexity: 'all'
      });
    } else {
      setActiveFilters((prev) => ({
        ...prev,
        [category]: value
      }));
    }
  };

  return (
    <>
      <Helmet>
        <title>Client Success Stories - meriodine | Real Testimonials &amp; Results</title>
        <meta name="description" content="Read verified testimonials from 500+ satisfied clients. See before/after results from residential emergencies and commercial projects. 30+ years of proven expertise with McDonald's, KFC, and homeowners across the region." />
      </Helmet>
      <div className="min-h-screen flex flex-col bg-background">
        <Header />
        
        <main className="flex-1 pt-16">
          <section className="bg-gradient-to-br from-primary to-secondary text-primary-foreground py-12 md:py-16 lg:py-20">
            <div className="max-w-7xl mx-auto px-4 lg:px-8">
              <div className="text-center max-w-3xl mx-auto">
                <div className="inline-flex items-center justify-center w-16 h-16 md:w-20 md:h-20 rounded-full bg-white/10 mb-4 md:mb-6">
                  <Icon name="MessageSquare" size={32} className="md:w-10 md:h-10" />
                </div>
                <h1 className="font-headline font-bold text-3xl md:text-4xl lg:text-5xl mb-4 md:mb-6">
                  Client Success Stories
                </h1>
                <p className="text-lg md:text-xl lg:text-2xl text-primary-foreground/90 font-body mb-6 md:mb-8">
                  Real results from real clients. See why homeowners and major brands trust Gert's 30+ years of expertise.
                </p>
                <div className="flex flex-col sm:flex-row gap-3 md:gap-4 justify-center">
                  <Button
                    variant="default"
                    size="lg"
                    iconName="Phone"
                    iconPosition="left"
                    onClick={() => window.location.href = 'tel:0827935785'}
                    className="bg-accent hover:bg-accent/90 text-accent-foreground emergency-cta">

                    Call 082 793 5785
                  </Button>
                  <Button
                    variant="outline"
                    size="lg"
                    iconName="Mail"
                    iconPosition="left"
                    onClick={() => window.location.href = '/contact'}
                    className="bg-white/10 hover:bg-white/20 border-white/30 text-white">

                    Get Your Quote
                  </Button>
                </div>
              </div>
            </div>
          </section>

          <section className="py-8 md:py-12 lg:py-16">
            <div className="max-w-7xl mx-auto px-4 lg:px-8">
              <StatsOverview />
              <CommercialHighlight />

              <div className="mb-8 md:mb-12">
                <div className="text-center mb-6 md:mb-8">
                  <h2 className="font-headline font-bold text-2xl md:text-3xl lg:text-4xl text-foreground mb-3 md:mb-4">
                    Before &amp; After Transformations
                  </h2>
                  <p className="text-muted-foreground text-base md:text-lg font-body max-w-2xl mx-auto">
                    Visual proof of our problem-solving capabilities and quality workmanship
                  </p>
                </div>

                <FilterBar
                  onFilterChange={handleFilterChange}
                  activeFilters={activeFilters} />


                {filteredProjects?.length > 0 ?
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 md:gap-8">
                    {filteredProjects?.map((project) =>
                  <BeforeAfterSlider key={project?.id} project={project} />
                  )}
                  </div> :

                <div className="text-center py-12 md:py-16">
                    <Icon name="Search" size={48} className="text-muted mx-auto mb-4" />
                    <p className="text-muted-foreground text-lg font-body">
                      No projects match your current filters. Try adjusting your selection.
                    </p>
                  </div>
                }
              </div>

              <div>
                <div className="text-center mb-6 md:mb-8">
                  <h2 className="font-headline font-bold text-2xl md:text-3xl lg:text-4xl text-foreground mb-3 md:mb-4">
                    What Our Clients Say
                  </h2>
                  <p className="text-muted-foreground text-base md:text-lg font-body max-w-2xl mx-auto">
                    Verified testimonials from satisfied residential and commercial clients
                  </p>
                </div>

                {filteredTestimonials?.length > 0 ?
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 md:gap-8">
                    {filteredTestimonials?.map((testimonial) =>
                  <TestimonialCard key={testimonial?.id} testimonial={testimonial} />
                  )}
                  </div> :

                <div className="text-center py-12 md:py-16">
                    <Icon name="Search" size={48} className="text-muted mx-auto mb-4" />
                    <p className="text-muted-foreground text-lg font-body">
                      No testimonials match your current filters. Try adjusting your selection.
                    </p>
                  </div>
                }
              </div>

              <div className="mt-12 md:mt-16 lg:mt-20 bg-gradient-to-br from-primary/5 to-secondary/5 rounded-lg border border-primary/20 p-6 md:p-8 lg:p-12 text-center">
                <div className="inline-flex items-center justify-center w-16 h-16 md:w-20 md:h-20 rounded-full bg-primary/10 mb-4 md:mb-6">
                  <Icon name="CheckCircle2" size={32} className="text-primary md:w-10 md:h-10" />
                </div>
                <h2 className="font-headline font-bold text-2xl md:text-3xl lg:text-4xl text-foreground mb-3 md:mb-4">
                  Ready to Join Our Success Stories?
                </h2>
                <p className="text-muted-foreground text-base md:text-lg font-body max-w-2xl mx-auto mb-6 md:mb-8">
                  Experience the same quality service and reliability that has earned us 500+ satisfied clients and partnerships with major brands.
                </p>
                <div className="flex flex-col sm:flex-row gap-3 md:gap-4 justify-center">
                  <Button
                    variant="default"
                    size="lg"
                    iconName="Phone"
                    iconPosition="left"
                    onClick={() => window.location.href = 'tel:0827935785'}
                    className="emergency-cta">

                    Emergency: 082 793 5785
                  </Button>
                  <Button
                    variant="outline"
                    size="lg"
                    iconName="Calendar"
                    iconPosition="left"
                    onClick={() => window.location.href = '/contact'}>

                    Schedule Service
                  </Button>
                </div>
              </div>
            </div>
          </section>
        </main>

        <Footer />
        <MobileEmergencyBar />
      </div>
    </>);

};

export default ClientSuccessStories;